import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFirebaseApp } from '../config/firebase';
import api from './api';

class WebPushService {
  constructor() {
    this.messaging = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.isSubscribed = false;
    this.subscription = null;
    this.permission = 'default';
  }

  async initialize() {
    try {
      if (!this.isSupported) {
        console.log('Web push notifications are not supported in this browser');
        return { success: false, error: 'Not supported' };
      }

      // Check permission
      this.permission = await this.checkPermission();
      
      // Don't fail initialization if permission is denied - just log it
      if (this.permission === 'denied') {
        console.log('Notification permission denied, but continuing initialization');
      }

      // Initialize Firebase messaging (only if permission is granted or default)
      const app = getFirebaseApp();
      
      if (app && (this.permission === 'granted' || this.permission === 'default')) {
        this.messaging = getMessaging(app);
        
        // Handle foreground messages
        onMessage(this.messaging, (payload) => {
          console.log('Foreground message received:', payload);
          this.showForegroundNotification(payload);
        });
      }

      // Check if already subscribed (only if permission is granted)
      if (this.permission === 'granted') {
        await this.checkSubscription();
      }

      return { success: true };
    } catch (error) {
      console.error('Web push service initialization error:', error);
      return { success: false, error: error.message };
    }
  }

  async checkPermission() {
    if (!this.isSupported) return 'denied';
    
    if (Notification.permission === 'default') {
      return 'default';
    } else if (Notification.permission === 'granted') {
      return 'granted';
    } else {
      return 'denied';
    }
  }

  async requestPermission() {
    try {
      if (!this.isSupported) {
        throw new Error('Web push notifications are not supported');
      }

      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        return { success: true };
      } else {
        console.log('Notification permission denied');
        return { success: false, error: 'Permission denied' };
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      return { success: false, error: error.message };
    }
  }

  async checkSubscription() {
    try {
      if (!this.isSupported || this.permission !== 'granted') {
        return { success: false, error: 'Not supported or permission denied' };
      }

      // Check if service worker is registered
      const registration = await navigator.serviceWorker.ready;
      
      // Check if already subscribed
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        this.subscription = subscription;
        this.isSubscribed = true;
        
        // Verify subscription with backend
        await this.verifySubscription(subscription);
        
        return { success: true, subscribed: true };
      } else {
        this.isSubscribed = false;
        return { success: true, subscribed: false };
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      return { success: false, error: error.message };
    }
  }

  async subscribe() {
    try {
      if (!this.isSupported) {
        throw new Error('Web push notifications are not supported');
      }

      if (this.permission !== 'granted') {
        const permissionResult = await this.requestPermission();
        if (!permissionResult.success) {
          throw new Error('Permission denied');
        }
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from backend
      const vapidResponse = await api.get('/web-push/vapid-public-key');
      if (!vapidResponse.data.success) {
        throw new Error('Failed to get VAPID public key');
      }

      const vapidPublicKey = vapidResponse.data.data.publicKey;
      
      // Convert VAPID key to Uint8Array
      const vapidKeyArray = this.urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyArray
      });

      this.subscription = subscription;
      this.isSubscribed = true;

      // Send subscription to backend
      const subscriptionData = {
        endpoint: subscription.endpoint,
        p256dh_key: this.arrayBufferToBase64(subscription.getKey('p256dh')),
        auth_token: this.arrayBufferToBase64(subscription.getKey('auth')),
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      };

      const response = await api.post('/web-push/subscribe', subscriptionData);
      
      if (response.data.success) {
        console.log('Successfully subscribed to web push notifications');
        return { success: true };
      } else {
        throw new Error('Failed to save subscription');
      }
    } catch (error) {
      console.error('Error subscribing to web push notifications:', error);
      this.isSubscribed = false;
      this.subscription = null;
      return { success: false, error: error.message };
    }
  }

  async unsubscribe() {
    try {
      if (!this.subscription) {
        return { success: true, message: 'No active subscription' };
      }

      // Unsubscribe from push manager
      await this.subscription.unsubscribe();
      
      // Remove from backend
      await api.post('/web-push/unsubscribe', {
        endpoint: this.subscription.endpoint
      });

      this.subscription = null;
      this.isSubscribed = false;

      console.log('Successfully unsubscribed from web push notifications');
      return { success: true };
    } catch (error) {
      console.error('Error unsubscribing from web push notifications:', error);
      return { success: false, error: error.message };
    }
  }

  async verifySubscription(subscription) {
    try {
      const response = await api.get('/web-push/subscriptions');
      if (response.data.success) {
        const subscriptions = response.data.data;
        const exists = subscriptions.some(sub => sub.endpoint === subscription.endpoint);
        
        if (!exists) {
          // Re-subscribe if not found in backend
          await this.subscribe();
        }
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
    }
  }

  showForegroundNotification(payload) {
    // Create a custom notification for foreground messages
    const notificationTitle = payload.notification?.title || 'Niswa Info Tech';
    const notificationOptions = {
      body: payload.notification?.body || 'You have a new notification',
      icon: payload.notification?.icon || '/images/logo192.png',
      badge: payload.notification?.badge || '/images/badge.png',
      image: payload.notification?.image,
      tag: payload.notification?.tag,
      requireInteraction: payload.notification?.requireInteraction || false,
      silent: payload.notification?.silent || false,
      data: payload.data || {},
      actions: payload.notification?.actions || []
    };

    // Show notification
    if (Notification.permission === 'granted') {
      const notification = new Notification(notificationTitle, notificationOptions);
      
      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        notification.close();
        
        // Handle click action
        const urlToOpen = payload.data?.url || '/';
        window.open(urlToOpen, '_blank');
      };
    }
  }

  // Utility functions
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // Get subscription status
  getSubscriptionStatus() {
    return {
      isSupported: this.isSupported,
      isSubscribed: this.isSubscribed,
      permission: this.permission
    };
  }

  // Get subscription data
  getSubscriptionData() {
    return this.subscription;
  }
}

// Create singleton instance
const webPushService = new WebPushService();

export default webPushService;
